
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { 
  Phone, Heart, Shield, Hospital, PhoneCall, Bandage, 
  UserRoundCog, StethoscopeIcon, Syringe, AlarmClock, BadgeAlert, 
  BookOpen, Info, Stethoscope, HeartPulse, Activity, Pill,
  Brain, ThermometerSun, ScrollText, AlertOctagon
} from "lucide-react";
import { toast } from "sonner";

const VirtualFirstAid = () => {
  const [selectedTab, setSelectedTab] = useState("emergency");

  const emergencyContacts = [
    { number: "15", name: "SAMU", icon: <Phone className="h-4 w-4" /> },
    { number: "17", name: "Police", icon: <PhoneCall className="h-4 w-4" /> },
    { number: "18", name: "Pompiers", icon: <PhoneCall className="h-4 w-4" /> },
    { number: "112", name: "Urgence", icon: <Phone className="h-4 w-4" /> },
  ];

  const emergencyItems = [
    {
      title: "Premiers secours",
      description: "Guide complet des gestes qui sauvent et des procédures d'urgence essentielles",
      items: [
        {
          name: "Instructions RCP",
          detail: "Étapes détaillées de la réanimation cardio-pulmonaire : 30 compressions thoraciques suivies de 2 insufflations"
        },
        {
          name: "Position latérale de sécurité",
          detail: "Procédure pour placer une personne inconsciente qui respire en PLS pour maintenir les voies respiratoires dégagées"
        },
        {
          name: "Gestion des hémorragies",
          detail: "Application d'une pression directe avec un tissu propre, surélévation du membre si possible"
        },
        {
          name: "Traitement des brûlures",
          detail: "Refroidir immédiatement sous l'eau froide pendant 20 minutes minimum, ne pas percer les cloques"
        },
        {
          name: "Étouffement",
          detail: "5 tapes dans le dos suivies de 5 compressions abdominales (Manœuvre de Heimlich)"
        },
        {
          name: "Perte de conscience",
          detail: "Vérifier la respiration, libérer les voies aériennes, appeler les secours"
        },
        {
          name: "Convulsions",
          detail: "Protéger la personne des blessures, ne rien mettre dans la bouche, chronométrer la crise"
        },
        {
          name: "Traumatisme crânien",
          detail: "Surveiller l'état de conscience, les pupilles et les signes neurologiques"
        }
      ],
      icon: <Heart className="h-5 w-5 text-red-500" />,
      category: "urgent",
    },
    {
      title: "Urgences médicales",
      description: "Reconnaître et réagir aux situations médicales urgentes nécessitant une intervention rapide",
      items: [
        {
          name: "Crise cardiaque",
          detail: "Douleur thoracique, essoufflement, sueurs froides - Appeler immédiatement le 15"
        },
        {
          name: "AVC",
          detail: "FAST : Face (asymétrie), Arm (faiblesse), Speech (trouble parole), Time (appeler urgences)"
        },
        {
          name: "Crise d'asthme",
          detail: "Utilisation du bronchodilatateur, position assise, respiration contrôlée"
        },
        {
          name: "Réaction allergique sévère",
          detail: "Utilisation de l'auto-injecteur d'adrénaline si prescrit, appel des secours"
        },
        {
          name: "Diabète - Hypo/Hyperglycémie",
          detail: "Hypoglycémie : sucre rapide. Hyperglycémie : vérifier la glycémie, insuline si prescrite"
        },
        {
          name: "Intoxication",
          detail: "Appeler le centre antipoison, ne pas faire vomir sans avis médical"
        },
        {
          name: "Déshydratation sévère",
          detail: "Signes : soif intense, urines foncées, fatigue - Réhydratation progressive"
        },
        {
          name: "Migraine sévère",
          detail: "Repos dans l'obscurité, médicaments prescrits, surveillance des signes d'alerte"
        }
      ],
      icon: <Hospital className="h-5 w-5 text-blue-500" />,
      category: "medical",
    },
    {
      title: "Prévention",
      description: "Mesures préventives et surveillance des paramètres de santé importants",
      items: [
        {
          name: "Signes vitaux normaux",
          detail: "Température : 36.5-37.5°C, Pouls : 60-100/min, Tension : 120/80 mmHg"
        },
        {
          name: "Checklist de sécurité",
          detail: "Vérification régulière des équipements, médicaments et dates de péremption"
        },
        {
          name: "Numéros d'urgence",
          detail: "Liste à jour des contacts médicaux et services d'urgence"
        },
        {
          name: "Plan d'évacuation",
          detail: "Procédure d'évacuation d'urgence et point de rassemblement"
        },
        {
          name: "Kit de premiers secours",
          detail: "Contenu minimum : pansements, désinfectant, ciseaux, gants, couverture de survie"
        },
        {
          name: "Vaccinations",
          detail: "Calendrier vaccinal à jour et rappels programmés"
        },
        {
          name: "Allergies connues",
          detail: "Liste des allergies et protocoles de prise en charge"
        },
        {
          name: "Médications régulières",
          detail: "Planning de prise des médicaments et dosages"
        }
      ],
      icon: <Shield className="h-5 w-5 text-green-500" />,
      category: "prevention",
    },
  ];

  const medicationSection = [
    {
      title: "Médicaments essentiels",
      description: "Guide des médicaments de base et leur utilisation appropriée",
      items: [
        {
          name: "Antidouleurs",
          detail: "Paracétamol, ibuprofène - Dosages et précautions d'emploi"
        },
        {
          name: "Anti-inflammatoires",
          detail: "Usage sur prescription, à prendre avec de la nourriture"
        },
        {
          name: "Antihistaminiques",
          detail: "Pour les allergies - Attention à la somnolence"
        },
        {
          name: "Antiseptiques",
          detail: "Pour la désinfection des plaies superficielles"
        },
        {
          name: "Pansements",
          detail: "Différentes tailles pour divers types de blessures"
        },
        {
          name: "Bandages",
          detail: "Élastiques et non-élastiques, techniques d'application"
        },
        {
          name: "Compresses stériles",
          detail: "Pour le nettoyage et la protection des plaies"
        },
        {
          name: "Sparadrap",
          detail: "Hypoallergénique pour la fixation des pansements"
        }
      ],
      icon: <Syringe className="h-5 w-5 text-purple-500" />,
    },
    {
      title: "Surveillance médicale",
      description: "Paramètres vitaux à surveiller et leur interprétation",
      items: [
        {
          name: "Prise de tension",
          detail: "Technique correcte et valeurs normales"
        },
        {
          name: "Mesure glycémie",
          detail: "Instructions pour le lecteur de glycémie"
        },
        {
          name: "Température",
          detail: "Types de thermomètres et zones de mesure"
        },
        {
          name: "Fréquence cardiaque",
          detail: "Comment prendre le pouls et valeurs normales"
        },
        {
          name: "Saturation en oxygène",
          detail: "Utilisation de l'oxymètre de pouls"
        },
        {
          name: "Fréquence respiratoire",
          detail: "Comment compter les respirations"
        },
        {
          name: "Niveau de conscience",
          detail: "Échelle de Glasgow et signes d'alerte"
        },
        {
          name: "Douleur (échelle)",
          detail: "Évaluation sur échelle de 0 à 10"
        }
      ],
      icon: <StethoscopeIcon className="h-5 w-5 text-indigo-500" />,
    }
  ];

  const remindersSection = [
    {
      title: "Rappels importants",
      description: "Planning et maintenance du matériel médical",
      items: [
        {
          name: "Vérification dates péremption",
          detail: "Contrôle mensuel des médicaments et produits"
        },
        {
          name: "Renouvellement ordonnances",
          detail: "Anticipation des renouvellements d'un mois"
        },
        {
          name: "Mise à jour vaccins",
          detail: "Calendrier vaccinal personnalisé"
        },
        {
          name: "Contrôle matériel médical",
          detail: "Vérification mensuelle des équipements"
        },
        {
          name: "RDV médicaux",
          detail: "Suivi des consultations régulières"
        },
        {
          name: "Stocks médicaments",
          detail: "Inventaire régulier des médicaments"
        },
        {
          name: "Maintenance équipements",
          detail: "Entretien du matériel médical"
        },
        {
          name: "Formation premiers secours",
          detail: "Recyclage des connaissances"
        }
      ],
      icon: <AlarmClock className="h-5 w-5 text-orange-500" />,
    },
    {
      title: "Alertes médicales",
      description: "Informations médicales critiques à connaître",
      items: [
        {
          name: "Allergies",
          detail: "Liste complète et conduites à tenir"
        },
        {
          name: "Contre-indications",
          detail: "Médicaments et traitements à éviter"
        },
        {
          name: "Interactions médicamenteuses",
          detail: "Associations déconseillées"
        },
        {
          name: "Conditions chroniques",
          detail: "Suivi des maladies chroniques"
        },
        {
          name: "Restrictions alimentaires",
          detail: "Régimes et allergies alimentaires"
        },
        {
          name: "Facteurs de risque",
          detail: "Surveillance particulière nécessaire"
        },
        {
          name: "Antécédents médicaux",
          detail: "Historique médical important"
        },
        {
          name: "Groupe sanguin",
          detail: "Type et rhésus à connaître"
        }
      ],
      icon: <BadgeAlert className="h-5 w-5 text-red-500" />,
    }
  ];

  const handleEmergencyCall = (number: string) => {
    window.location.href = `tel:${number}`;
    toast.success(`Appel d'urgence au ${number} initié`);
  };

  return (
    <div className="container mx-auto px-4 py-8 space-y-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold">Trousse de secours virtuelle</h1>
          <p className="text-muted-foreground mt-2">
            Guide complet des premiers secours et informations médicales essentielles
          </p>
        </div>
        
        <div className="w-full md:w-auto grid grid-cols-2 md:grid-cols-4 gap-3">
          {emergencyContacts.map((contact) => (
            <Button
              key={contact.number}
              onClick={() => handleEmergencyCall(contact.number)}
              className="flex items-center justify-center h-12 w-full md:w-[130px] px-3"
              variant="destructive"
              size="default"
            >
              <div className="flex items-center gap-2">
                {contact.icon}
                <div className="flex flex-col">
                  <span className="text-sm font-semibold">{contact.number}</span>
                  <span className="text-xs">{contact.name}</span>
                </div>
              </div>
            </Button>
          ))}
        </div>
      </div>

      <Tabs defaultValue="emergency" className="space-y-4" value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <TabsTrigger value="emergency">Urgences</TabsTrigger>
          <TabsTrigger value="medication">Médicaments</TabsTrigger>
          <TabsTrigger value="monitoring">Surveillance</TabsTrigger>
          <TabsTrigger value="reminders">Rappels</TabsTrigger>
        </TabsList>

        <TabsContent value="emergency" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-bold">Guide des premiers secours</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[600px] pr-4">
                <div className="space-y-8">
                  {emergencyItems.map((section) => (
                    <div key={section.title} className="space-y-4">
                      <div className="flex items-center gap-2">
                        {section.icon}
                        <div>
                          <h3 className="font-semibold text-lg">{section.title}</h3>
                          <p className="text-sm text-muted-foreground">{section.description}</p>
                        </div>
                        <Badge
                          variant={
                            section.category === "urgent"
                              ? "destructive"
                              : section.category === "medical"
                              ? "default"
                              : "secondary"
                          }
                        >
                          {section.category}
                        </Badge>
                      </div>
                      <div className="grid gap-3">
                        {section.items.map((item) => (
                          <Card key={item.name} className="p-4 hover:bg-accent transition-colors cursor-pointer">
                            <div className="space-y-2">
                              <h4 className="font-medium">{item.name}</h4>
                              <p className="text-sm text-muted-foreground">{item.detail}</p>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="medication" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-bold">Gestion des médicaments</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[600px] pr-4">
                <div className="space-y-8">
                  {medicationSection.map((section) => (
                    <div key={section.title} className="space-y-4">
                      <div className="flex items-center gap-2">
                        {section.icon}
                        <div>
                          <h3 className="font-semibold text-lg">{section.title}</h3>
                          <p className="text-sm text-muted-foreground">{section.description}</p>
                        </div>
                      </div>
                      <div className="grid gap-3">
                        {section.items.map((item) => (
                          <Card key={item.name} className="p-4 hover:bg-accent transition-colors cursor-pointer">
                            <div className="space-y-2">
                              <h4 className="font-medium">{item.name}</h4>
                              <p className="text-sm text-muted-foreground">{item.detail}</p>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="monitoring" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-bold">Surveillance des paramètres vitaux</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <UserRoundCog className="h-5 w-5 text-primary" />
                    <div>
                      <h3 className="font-semibold">Paramètres à surveiller</h3>
                      <p className="text-sm text-muted-foreground">Valeurs normales de référence</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span>Tension artérielle</span>
                      <Badge variant="outline">120/80 mmHg</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Fréquence cardiaque</span>
                      <Badge variant="outline">60-100 bpm</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Température</span>
                      <Badge variant="outline">37°C</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Saturation en oxygène</span>
                      <Badge variant="outline">95-100%</Badge>
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Bandage className="h-5 w-5 text-primary" />
                    <div>
                      <h3 className="font-semibold">Valeurs normales</h3>
                      <p className="text-sm text-muted-foreground">Paramètres biologiques de référence</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span>Glycémie à jeun</span>
                      <Badge variant="outline">0.7-1.1 g/L</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Fréquence respiratoire</span>
                      <Badge variant="outline">12-20/min</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>IMC</span>
                      <Badge variant="outline">18.5-25</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Pression artérielle</span>
                      <Badge variant="outline">≤ 140/90</Badge>
                    </div>
                  </div>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reminders" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-bold">Rappels et alertes</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[600px] pr-4">
                <div className="space-y-8">
                  {remindersSection.map((section) => (
                    <div key={section.title} className="space-y-4">
                      <div className="flex items-center gap-2">
                        {section.icon}
                        <div>
                          <h3 className="font-semibold text-lg">{section.title}</h3>
                          <p className="text-sm text-muted-foreground">{section.description}</p>
                        </div>
                      </div>
                      <div className="grid gap-3">
                        {section.items.map((item) => (
                          <Card key={item.name} className="p-4 hover:bg-accent transition-colors cursor-pointer">
                            <div className="space-y-2">
                              <h4 className="font-medium">{item.name}</h4>
                              <p className="text-sm text-muted-foreground">{item.detail}</p>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default VirtualFirstAid;

